import type { ResolverTag } from '@ps/web-core-enums';
import { StarsLink } from '@ps/web-shared-ui';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import URL from 'url-parse';
import type { Primitive } from 'utility-types';

import type { Options } from '@swf/core/hooks/useResolver';
import useResolver from '@swf/core/hooks/useResolver';
import useTrackers from '@swf/core/hooks/useTrackers';
import { select } from '@swf/core/store';

type Query = Record<string, Primitive>;

interface Props {
  resolverTag: ResolverTag | string;
  query?: Query;
  options?: Options;
  className?: string;
}

const ResolverLink: React.FC<Props> = ({ resolverTag, children, query, options, className }) => {
  const tag = useResolver(resolverTag, options);
  const { linkClicked } = useTrackers();
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);

  const url = useMemo(() => {
    if (!tag) return tag;
    const parsed = new URL(tag, true);
    Object.assign(parsed.query, query);
    return parsed.toString();
  }, [query, tag]);

  const onClick = useCallback(() => {
    isMobileEmbedded && window.starsweb?.app.execute(`resolve/${resolverTag}`);
    linkClicked({ destinationUrl: url, resolverTag });
  }, [isMobileEmbedded, linkClicked, resolverTag, url]);

  return isMobileEmbedded ? (
    <StarsLink onClick={onClick} className={className}>
      {children}
    </StarsLink>
  ) : (
    <StarsLink
      target="_blank"
      onClick={onClick}
      rel="noopener noreferrer"
      href={`${isDesktopEmbedded ? 'external-' : ''}${url}`}
      className={className}
    >
      {children}
    </StarsLink>
  );
};

export default ResolverLink;
